"use client"

import React, { useCallback, useState } from "react"
import styled, { css } from "styled-components"
import { Label } from "@jobintrans/base-ui.components.atoms.typography"
import { Box } from "@jobintrans/base-ui.components.atoms._atoms"
import { BaseStyles } from "@jobintrans/base-ui.types.base-styles"
import Checkbox from "@jobintrans/base-ui.components.atoms.checkbox"
import { ErrorMessage } from "@jobintrans/base-ui.components.atoms.input"
import Button from "@jobintrans/base-ui.components.atoms.button"

export type RadioCheckboxesProps = BaseStyles & {
  name: string
  value: any[]
  options: number[][] | string[][]
  label?: string
  labelIcon?: string
  seeMore?: number
  seeMoreLabel?: string
  seeLessLabel?: string
  onChange?: (e: { name: string; value: any }) => void
  error?: {
    type?: string
    message?: string
  }
  $type?: "primary" | "secondary"
  $iconColor?: string
}

const CheckboxesGroup = React.forwardRef<HTMLDivElement, RadioCheckboxesProps>(
  function CheckboxesGroup(
    {
      name,
      value = [],
      options = [],
      label,
      error,
      labelIcon,
      onChange = () => {},
      seeMore,
      seeMoreLabel,
      seeLessLabel,
      $type = "primary",
      $iconColor,
      ...rest
    },
    ref,
  ) {
    const [seeAll, setSeeAll] = useState<boolean | null>(seeMore ? false : null)

    const handleOnChange = (e: any[]) => {
      onChange({
        name,
        value: e,
      })
    }

    const handleClick = useCallback(
      ({ name, value: elValue }: { name: string; value: boolean }) => {
        if (elValue) {
          const arr = [...value, name]
          handleOnChange(arr)
        } else {
          handleOnChange(value.filter(e => e !== name))
        }
      },
      [handleOnChange, value, options],
    )

    return (
      <Wrapper ref={ref} {...rest}>
        {label && (
          <Label icon={labelIcon} $marginBottom="S">
            {label}
          </Label>
        )}
        <CheckboxesContent $type={$type}>
          {options
            .filter((e, index) => (seeMore && !seeAll ? index <= seeMore : true))
            .map((el: number[] | string[]) => (
              <Checkbox
                key={`${el[1]}`}
                name={`${el[1]}`}
                value={value?.includes(el[1])}
                onChange={handleClick}
                label={`${el[0]}`}
                icon={`${el[2]}`}
                $type={$type}
                $iconColor={$iconColor}
                $iconSize="S"
              />
            ))}
        </CheckboxesContent>
        {seeAll !== null && (
          <Button
            icon="plus"
            $marginLeft="auto"
            $marginRight="auto"
            $marginTop="S"
            onClick={() => setSeeAll(!seeAll)}
            $type="fourth"
            $padding="0"
          >
            {seeAll ? seeLessLabel : seeMoreLabel}
          </Button>
        )}
        {error && (
          <ErrorMessage $marginLeft="0" $marginTop="S">
            {error?.message ?? "To pole jest wymagane"}
          </ErrorMessage>
        )}
      </Wrapper>
    )
  },
)

export default CheckboxesGroup

const CheckboxesContent = styled.div<{
  $type: "primary" | "secondary"
}>`
  display: flex;
  flex-wrap: wrap;
  grid-gap: ${({ theme }) => theme.space.XS};

  ${({ $type }) =>
    $type === "secondary" &&
    css`
      grid-gap: ${({ theme }) => theme.space.XXS};
    `}
`

const Wrapper = styled(Box)`
  * {
    box-sizing: border-box;
  }
`
